import React, { useState, useEffect, useCallback } from 'react';
import { adminPanelService } from '../services/adminPanelService';
import PaymentGatewayConfig from './PaymentGatewayConfig';
import QRCode from 'qrcode';
import { API_BASE_URL } from '../config/supabase';

interface AdminDashboardProps {
  adminData?: any;
  onLogout?: () => void;
}

// QR Code component for each user
const UserQRCode: React.FC<{ user: any }> = ({ user }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [qrLoginUrl, setQrLoginUrl] = useState<string>('');
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (showQR && user?.id) {
      // Generate URL for QR login - points to backend endpoint with user ID
      const loginUrl = `${API_BASE_URL}/user-auth/qr-login/${user.id}`;
      setQrLoginUrl(loginUrl);
      
      // Generate QR code
      QRCode.toDataURL(loginUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      .then((url: string) => {
        setQrCodeUrl(url);
      })
      .catch((err: Error) => {
        console.error('Error generating QR code:', err);
      });
    }
  }, [showQR, user]);

  const handleDownloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `qr-code-${user?.user_name || user?.id || 'user'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenLink = () => {
    if (qrLoginUrl) {
      window.open(qrLoginUrl, '_blank');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowQR(!showQR)}
        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
      >
        QR Code
      </button>
      
      {showQR && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowQR(false)}
          />
          {/* QR Code Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-6 border border-gray-200 max-w-sm mx-4">
              <div className="text-center">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">User Login QR Code</h3>
                <p className="text-xs text-gray-600 mb-4">Scan to login as {user?.user_name || 'user'}</p>
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48 mx-auto mb-4" />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center mx-auto mb-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleOpenLink}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>🔗</span>
                    Open Link
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    disabled={!qrCodeUrl}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <span>📥</span>
                    Download QR Code
                  </button>
                  <button
                    onClick={() => setShowQR(false)}
                    className="px-4 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ adminData, onLogout }) => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'transactions' | 'rates' | 'system' | 'payment-gateways' | 'cron-management' | 'api-health'>('overview');
  const [showPaymentGatewayConfig, setShowPaymentGatewayConfig] = useState(false);
  
  // User management state
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersPagination, setUsersPagination] = useState<any>(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersStatusFilter, setUsersStatusFilter] = useState<'active' | 'inactive' | ''>('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      setUsersError('');
      
      const filters: any = {
        page: usersPage,
        limit: 20
      };
      
      if (usersSearch) {
        filters.search = usersSearch;
      }
      
      if (usersStatusFilter) {
        filters.status = usersStatusFilter;
      }
      
      const response = await adminPanelService.getUsers(filters);
      
      if (response.success && response.data) {
        setUsers(response.data || []);
        setUsersPagination(response.pagination || null);
      } else {
        setUsersError(response.error || 'Failed to load users');
      }
    } catch (err) {
      setUsersError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, usersSearch, usersStatusFilter]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, loadUsers]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminPanelService.getDashboardOverview();
      
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(parseFloat(amount));
  };

  const StatCard = ({ title, value, subtitle, color = 'blue' }: any) => (
    <div className={`bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl p-6 text-white`}>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <div className="text-3xl font-bold mb-1">{value}</div>
      {subtitle && <p className="text-sm opacity-90">{subtitle}</p>}
    </div>
  );

  const TabButton = ({ tab, label, icon }: any) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
        activeTab === tab
          ? 'bg-blue-600 text-white'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white/90 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-4 py-4">
            <TabButton tab="overview" label="Overview" icon="📊" />
            <TabButton tab="users" label="Users" icon="👥" />
            <TabButton tab="transactions" label="Transactions" icon="💰" />
            <TabButton tab="rates" label="Metal Rates" icon="🥇" />
            <TabButton tab="payment-gateways" label="Payment Gateways" icon="💳" />
            <TabButton tab="cron-management" label="Cron Management" icon="⚙️" />
            <TabButton tab="api-health" label="API Health" icon="🔍" />
            <TabButton tab="system" label="System" icon="🔧" />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Dashboard Overview</h2>
            
            {/* Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Users"
                value={dashboardData.users?.total || 0}
                subtitle={`${dashboardData.users?.active || 0} active`}
                color="green"
              />
              <StatCard
                title="Total Transactions"
                value={dashboardData.transactions?.total || 0}
                subtitle={formatCurrency(dashboardData.transactions?.totalAmount || '0')}
                color="blue"
              />
              <StatCard
                title="Metal Rates"
                value={dashboardData.metalRates?.total || 0}
                subtitle={`${dashboardData.metalRates?.active || 0} active`}
                color="purple"
              />
              <StatCard
                title="Cron Jobs"
                value={dashboardData.cronExecutions?.total || 0}
                subtitle={`${dashboardData.cronExecutions?.successRate || 0}% success`}
                color="orange"
              />
            </div>

            {/* Transaction Status Breakdown */}
            {dashboardData.transactions && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Transaction Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboardData.transactions.byStatus || {}).map(([status, count]: [string, any]) => (
                    <div key={status} className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{status}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Method Breakdown */}
            {dashboardData.transactions && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboardData.transactions.byPaymentMethod || {}).map(([method, count]: [string, any]) => (
                    <div key={method} className="text-center">
                      <div className="text-2xl font-bold text-green-600">{count}</div>
                      <div className="text-sm text-gray-600">{method}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Management</h2>
            
            {/* Search and Filters */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name, mobile, or email..."
                  value={usersSearch}
                  onChange={(e) => {
                    setUsersSearch(e.target.value);
                    setUsersPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={usersStatusFilter}
                onChange={(e) => {
                  setUsersStatusFilter(e.target.value as 'active' | 'inactive' | '');
                  setUsersPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Error Message */}
            {usersError && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {usersError}
              </div>
            )}

            {/* Loading State */}
            {usersLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-600">Loading users...</p>
              </div>
            ) : (
              <>
                {/* Users Table */}
                {users.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QR Code</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{user.user_name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{user.unique_id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.mobile_number || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.email_id || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.user_city || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{user.user_state || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  user.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <UserQRCode user={user} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {usersPagination && usersPagination.totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(usersPagination.currentPage - 1) * usersPagination.itemsPerPage + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(usersPagination.currentPage * usersPagination.itemsPerPage, usersPagination.totalItems)}
                          </span>{' '}
                          of <span className="font-medium">{usersPagination.totalItems}</span> users
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setUsersPage(prev => Math.max(1, prev - 1))}
                            disabled={usersPagination.currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setUsersPage(prev => Math.min(usersPagination.totalPages, prev + 1))}
                            disabled={usersPagination.currentPage === usersPagination.totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No users found.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Transaction Management</h2>
            <p className="text-gray-600">Transaction management features coming soon...</p>
          </div>
        )}

        {activeTab === 'rates' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Metal Rates Management</h2>
            <p className="text-gray-600">Metal rates management features coming soon...</p>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">System Monitoring</h2>
            <p className="text-gray-600">System monitoring features coming soon...</p>
          </div>
        )}

        {activeTab === 'payment-gateways' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Payment Gateway Management</h2>
              <button
                onClick={() => setShowPaymentGatewayConfig(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Configure Gateways
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Manage payment gateway configurations, enable/disable gateways, and set priorities.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">Available Payment Gateways</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Razorpay:</strong> Primary payment gateway with UPI, cards, and net banking support</li>
                <li>• <strong>Sahkar PG:</strong> Alternative payment gateway with comprehensive payment options</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'cron-management' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cron Management</h2>
            <p className="text-gray-600 mb-4">
              Monitor and manage automated tasks including rate fetching and token refresh.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <h3 className="font-medium text-blue-800 mb-2">Available Cron Jobs</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Rate Fetching:</strong> Fetches gold/silver rates every minute</li>
                <li>• <strong>Token Refresh:</strong> Refreshes Augmont API token daily at 6:00 AM IST</li>
              </ul>
            </div>
            <div className="flex space-x-4">
              <a
                href="/cron"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Cron Management
              </a>
            </div>
          </div>
        )}

        {activeTab === 'api-health' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">API Health Monitor</h2>
            <p className="text-gray-600 mb-4">
              Monitor the health and performance of payment gateways and external APIs.
            </p>
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <h3 className="font-medium text-green-800 mb-2">Monitored Services</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• <strong>Payment Gateways:</strong> Razorpay, Sahkar PG status monitoring</li>
                <li>• <strong>Augmont API:</strong> Gold/silver rates API health</li>
                <li>• <strong>Database:</strong> Connection and query performance</li>
              </ul>
            </div>
            <div className="flex space-x-4">
              <a
                href="/api-health"
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Open API Health Monitor
              </a>
            </div>
          </div>
        )}
      </main>

      {/* Payment Gateway Configuration Modal */}
      {showPaymentGatewayConfig && (
        <PaymentGatewayConfig onClose={() => setShowPaymentGatewayConfig(false)} />
      )}
    </div>
  );
};

export default AdminDashboard; 